import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IOrderingGame } from './ordering-game.interface';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl3-ordering-game';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IOrderingGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class OrderingGameCoreService {
  private _loading = false;

  /**
   * @name orderingGameList$
   * @description emits stored list of the companies
   */

  orderingGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    this.refresh();
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getOrderingGames(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`orderings-game/list`, params || {})
      .subscribe((data: IOrderingGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addOrderingGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('orderings-game/add', dataInput || {}).pipe(
      tap((data: IOrderingGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateOrderingGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('orderings-game/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IOrderingGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteOrderingGame(data): Observable<any> {
    this._loading = true;
    return this._api.delete(`orderings-game`, { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
        this._loading = false;
      }),
    );
  }

  public getOrderingGameById(
    orderingGameId: string,
  ): Observable<IOrderingGame> {
    return this._api.get(`orderings-game/${orderingGameId}`);
  }

  public getOrderingGameInfo(id: string): IOrderingGame {
    const orderingGame = store.query(getEntity(id));
    return orderingGame;
  }

  public resolveOrderingGameId(orderingGame: string | IOrderingGame): string {
    let orderingGameId: string | IOrderingGame = '';
    if (orderingGame) {
      orderingGameId = orderingGame;
    }
    if (orderingGame['_id']) {
      orderingGameId = orderingGame['_id'];
    }
    return orderingGameId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getOrderingGames();
    }
  }
}

interface IOrderingGameListResponse {
  list: IOrderingGame[];
  totalCount: number;
}
